import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { BilingualText } from "../components/ui/BilingualText";
import { EntityDrawer } from "../components/ui/EntityDrawer";
import { PageHeader } from "../components/ui/PageHeader";
import { api, ApiError } from "../lib/api";
import type {
  AiJobFullProfile,
  JobCreateBody,
  JobSummary,
} from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// Job catalog page (M10.7). The Job is the role template that
// Positions (M10.6) instantiate. Simpler than Position because Job
// has no assignee, no reports-to, no dates — just the template.
//
// LE id from ?le=<uuid> (same as the other M10 pages, until M11
// lands the LE picker).

function NewJobDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<JobCreateBody>({
    legal_entity_id: legalEntityId,
    code: "",
    title_en: "",
    title_ar: "",
    description: "",
    job_family: "",
    is_supervisory: false,
  });

  // Generated competencies, mutable by edits to the form. Each
  // section is a flat array; on Create we POST each entry in
  // sequence after the job itself is created.
  const [profile, setProfile] = useState<AiJobFullProfile | null>(null);

  // Per-section tracking of POST results, so the user sees which
  // competencies succeeded vs failed if any POST errors.
  const [postProgress, setPostProgress] = useState<{
    written: number;
    total: number;
    errors: string[];
  } | null>(null);

  // "Generate full profile from title" — single AI call that fills
  // metadata + description + all 5 competency arrays.
  const aiFill = useMutation({
    mutationFn: () =>
      api.aiJobFullProfile({
        title_en: form.title_en,
        legal_entity_id: legalEntityId,
      }),
    onSuccess: (data) => {
      setForm((f) => ({
        ...f,
        code: data.code,
        title_ar: data.title_ar,
        description: data.description,
        job_family: data.job_family,
        is_supervisory: data.is_supervisory,
      }));
      setProfile(data);
    },
  });

  // The "Create" button writes:
  //   1. POST /api/jobs with the form (including description)
  //   2. For each competency in each kind, POST .../competencies
  // Partial failures are surfaced but the dialog stays open so the
  // user can choose to keep what landed.
  const createAll = useMutation({
    mutationFn: async () => {
      const job = await api.createJob({
        ...form,
        job_family: form.job_family || null,
      });

      if (!profile) {
        return { job, written: 0, total: 0, errors: [] };
      }

      // Flatten all competencies into a single sequence so we get
      // a deterministic progress count.
      const competencies: Array<{
        kind: "skill" | "kpi" | "certificate" | "test" | "objective";
        body: Record<string, unknown>;
      }> = [
        ...profile.skills.map((s, i) => ({
          kind: "skill" as const,
          body: { ...s, sequence_index: i },
        })),
        ...profile.kpis.map((k, i) => ({
          kind: "kpi" as const,
          body: { ...k, sequence_index: i },
        })),
        ...profile.certificates.map((c, i) => ({
          kind: "certificate" as const,
          body: { ...c, sequence_index: i },
        })),
        ...profile.tests.map((t, i) => ({
          kind: "test" as const,
          body: { ...t, sequence_index: i },
        })),
        ...profile.objectives.map((o, i) => ({
          kind: "objective" as const,
          body: { ...o, sequence_index: i },
        })),
      ];

      let written = 0;
      const errors: string[] = [];
      for (const c of competencies) {
        try {
          await api.createJobCompetency(job.id, {
            kind: c.kind,
            ...(c.body as { name_en: string }),
          });
          written += 1;
          setPostProgress({
            written,
            total: competencies.length,
            errors,
          });
        } catch (e) {
          const msg =
            e instanceof ApiError ? e.detail : String((e as Error).message);
          errors.push(`${c.kind}: ${msg}`);
        }
      }

      return { job, written, total: competencies.length, errors };
    },
    onSuccess: (result) => {
      if (result.errors.length === 0) {
        onCreated();
        onClose();
      } else {
        // Job + some competencies landed but others failed. Leave
        // the dialog open so the user can read the errors and
        // decide. The job itself exists at this point.
        setPostProgress({
          written: result.written,
          total: result.total,
          errors: result.errors,
        });
      }
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setPostProgress(null);
    createAll.mutate();
  };

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("job.new_job")}
      description={t("job.new_job_description")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("job.cancel")}
          </button>
          <button
            type="submit"
            form="new-job-form"
            disabled={createAll.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {createAll.isPending
              ? postProgress
                ? t("job.saving_progress", {
                    written: postProgress.written,
                    total: postProgress.total,
                  })
                : t("job.creating")
              : profile
              ? t("job.create_with_competencies", {
                  count:
                    profile.skills.length +
                    profile.kpis.length +
                    profile.certificates.length +
                    profile.tests.length +
                    profile.objectives.length,
                })
              : t("job.create")}
          </button>
        </div>
      }
    >
      <form id="new-job-form" onSubmit={submit} className="space-y-4">
          {/* Title first — the AI generator keys off this. */}
          <label className="block text-sm">
            <span className="font-medium text-text-primary">
              {t("job.title_en")} <span className="text-rose-600">*</span>
            </span>
            <input
              required
              type="text"
              value={form.title_en}
              onChange={(e) =>
                setForm({ ...form, title_en: e.target.value })
              }
              placeholder={t("job.title_en_placeholder")}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
            />
          </label>

          {/* AI fill — disabled until the title is filled in. On
              click, generates the FULL profile: metadata +
              description + 5 competency arrays. Everything is
              pre-filled below and stays editable; Create then
              writes all of it in one click. */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-ai-subtle bg-ai-bg px-3 py-2.5">
            <Sparkles size={14} className="text-ai" aria-hidden />
            <p className="flex-1 text-xs text-text-secondary">
              {t("job.ai_fill_hint")}
            </p>
            <button
              type="button"
              onClick={() => aiFill.mutate()}
              disabled={!form.title_en.trim() || aiFill.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-ai-accent/40 bg-surface-default px-2.5 py-1.5 text-xs font-medium text-ai-deep hover:bg-ai-bg disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles size={12} aria-hidden />
              {aiFill.isPending
                ? t("job.ai_generating")
                : profile
                ? t("job.ai_regenerate")
                : t("job.ai_generate_full_profile")}
            </button>
          </div>
          {aiFill.error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden />
              {(aiFill.error as Error).message}
            </div>
          )}

          <label className="block text-sm">
            <span className="font-medium text-text-primary">
              {t("job.title_ar")}
            </span>
            <input
              type="text"
              value={form.title_ar}
              onChange={(e) =>
                setForm({ ...form, title_ar: e.target.value })
              }
              placeholder={t("job.title_ar_placeholder")}
              dir="rtl"
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-text-primary">
              {t("job.code")} <span className="text-rose-600">*</span>
            </span>
            <input
              required
              type="text"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              placeholder={t("job.code_placeholder")}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm font-mono text-text-primary"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-text-primary">
              {t("job.job_family")}
            </span>
            <input
              type="text"
              value={form.job_family ?? ""}
              onChange={(e) => setForm({ ...form, job_family: e.target.value })}
              placeholder={t("job.job_family_placeholder")}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_supervisory ?? false}
              onChange={(e) =>
                setForm({ ...form, is_supervisory: e.target.checked })
              }
              className="h-4 w-4 rounded border-border-subtle"
            />
            <span className="text-text-primary">
              {t("job.supervisory_role")}
            </span>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-text-primary">
              {t("job.description")}
            </span>
            <textarea
              value={form.description ?? ""}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={6}
              placeholder={t("job.description_placeholder")}
              className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
            />
          </label>

          {/* Generated competency preview — collapsible per section,
              with edit-in-place not wired yet (v1 ships read-only
              preview; you can prune in the detail page). */}
          {profile && (
            <div className="rounded-lg border border-ai-subtle bg-ai-bg p-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-ai-deep">
                <Sparkles size={12} aria-hidden />
                {t("job.ai_competencies_label")}
              </div>
              <CompetencyPreview
                kind={t("job.section_skills")}
                items={profile.skills.map((s) => ({
                  primary: s.name_en,
                  secondary: s.description,
                  tag: s.level,
                }))}
              />
              <CompetencyPreview
                kind={t("job.section_kpis")}
                items={profile.kpis.map((k) => ({
                  primary: k.name_en,
                  secondary: k.measurement,
                  tag: k.weight != null ? `${Math.round(k.weight * 100)}%` : null,
                }))}
              />
              <CompetencyPreview
                kind={t("job.section_certificates")}
                items={profile.certificates.map((c) => ({
                  primary: c.name_en,
                  secondary: c.description,
                  tag: c.importance,
                }))}
              />
              <CompetencyPreview
                kind={t("job.section_tests")}
                items={profile.tests.map((tt) => ({
                  primary: tt.name_en,
                  secondary: tt.description,
                  tag: null,
                }))}
              />
              <CompetencyPreview
                kind={t("job.section_objectives")}
                items={profile.objectives.map((o) => ({
                  primary: o.name_en,
                  secondary: o.measurement,
                  tag: o.weight != null ? `${Math.round(o.weight * 100)}%` : null,
                }))}
              />
            </div>
          )}

          {createAll.error && (
            <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">
              {(createAll.error as Error).message}
            </p>
          )}

          {/* Post-Create progress summary — shown when SOME
              competencies failed to write so the user knows the
              job exists but didn't get every competency. */}
          {postProgress && postProgress.errors.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle size={12} aria-hidden />
                {t("job.job_created_partial", {
                  written: postProgress.written,
                  total: postProgress.total,
                })}
              </div>
              <p className="mt-1">{t("job.competencies_couldnt_save")}</p>
              <ul className="mt-1 list-disc ps-5">
                {postProgress.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {postProgress &&
            postProgress.errors.length === 0 &&
            postProgress.written > 0 && (
              <div className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                <CheckCircle2 size={12} aria-hidden />
                {t("job.job_competencies_saved", {
                  count: postProgress.written,
                })}
              </div>
            )}
      </form>
    </EntityDrawer>
  );
}

function JobRow({
  job,
  onDelete,
}: {
  job: JobSummary;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <tr className="hover:bg-slate-50">
      <td className="px-4 py-3 font-mono text-sm text-slate-700">
        <Link to={`/people/jobs/${job.id}`} className="hover:underline">
          {job.code}
        </Link>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900">
        <Link to={`/people/jobs/${job.id}`} className="hover:underline">
          <BilingualText
            en={job.title_en}
            ar={job.title_ar}
            primaryClassName="text-slate-900"
            secondaryClassName="text-xs text-slate-500"
          />
        </Link>
      </td>
      <td className="px-4 py-3 text-sm text-slate-700">{job.job_family ?? "—"}</td>
      <td className="px-4 py-3 text-sm text-slate-700">{job.job_type}</td>
      <td className="px-4 py-3 text-sm">
        {job.is_supervisory ? (
          <Badge tone="sky">{t("job.badge_supervisor")}</Badge>
        ) : (
          <Badge tone="slate">{t("job.badge_ic")}</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-end">
        <button
          type="button"
          onClick={() => onDelete(job.id)}
          className="inline-flex items-center gap-1 rounded p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
          aria-label={t("job.delete_job")}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

export function JobsPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [showNew, setShowNew] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const qc = useQueryClient();

  const jobsQ = useQuery({
    queryKey: ["jobs", le],
    queryFn: () => api.listJobs(le),
    enabled: !!le,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteJob(id),
    onSuccess: () => {
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  if (gate) return gate;
  if (jobsQ.isLoading) return <Loading />;
  if (jobsQ.error) return <ErrorPanel message={(jobsQ.error as Error).message} />;

  const jobs = jobsQ.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t("job.page_title")}
        subtitle={`(${jobs.length})`}
        actions={
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> {t("job.new_job")}
          </button>
        }
      />

      {errorMsg && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {errorMsg}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50">
            <tr className="text-start text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 text-start">{t("job.col_code")}</th>
              <th className="px-4 py-3 text-start">{t("job.col_title")}</th>
              <th className="px-4 py-3 text-start">{t("job.col_family")}</th>
              <th className="px-4 py-3 text-start">{t("job.col_type")}</th>
              <th className="px-4 py-3 text-start">{t("job.col_role")}</th>
              <th className="px-4 py-3 text-end">{t("job.col_actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map((j) => (
              <JobRow key={j.id} job={j} onDelete={(id) => deleteMutation.mutate(id)} />
            ))}
            {jobs.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-12 text-center text-sm italic text-text-tertiary"
                >
                  {t("job.no_jobs_yet")}{" "}
                  <span className="not-italic">
                    {t(
                      "job.no_jobs_yet_hint",
                      "— click 'New job' above to define your first role template.",
                    )}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewJobDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ["jobs"] })}
        />
      )}
    </div>
  );
}

// Read-only preview block for one competency section inside the
// New Job dialog. Compact: one row per entry with the primary
// name, a one-line secondary detail, and an optional tag.
function CompetencyPreview({
  kind,
  items,
}: {
  kind: string;
  items: Array<{
    primary: string;
    secondary: string | null;
    tag: string | null;
  }>;
}) {
  if (items.length === 0) return null;
  return (
    <details className="group mt-2 first:mt-0">
      <summary className="cursor-pointer list-none rounded px-2 py-1 text-xs font-medium text-text-primary hover:bg-surface-default">
        <span className="me-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-blue text-[10px] text-white">
          {items.length}
        </span>
        {kind}
        <span className="ms-1 text-text-tertiary">— click to expand</span>
      </summary>
      <ul className="mt-1 space-y-1 ps-6 text-xs">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="font-medium text-text-primary">{it.primary}</span>
            {it.tag && (
              <span className="rounded border border-border-subtle bg-surface-default px-1 text-[10px] uppercase tracking-wider text-text-tertiary">
                {it.tag}
              </span>
            )}
            {it.secondary && (
              <span className="text-text-tertiary">— {it.secondary}</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
